import React, { useEffect, useState } from 'react'
import st from './CallAnalytics.module.scss'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import useLocalization from 'hooks/useLocalization'
import { observer } from 'mobx-react'
import CallStatistic from './CallStatistic/CallStatistic'
import AverageStatistic from './AverageStatistic/AverageStatistic'
import { superAxiosRequest } from 'axiosApi'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import CallList from './CallList/CallList'
import Pagination from 'components/UI/Pagination/Pagination'
import TotalStatistic from './TotalStatistic/TotalStatistic'
import { useHistory } from 'react-router-dom'

const callColors = [
  { id: 1, color: '#55B1F3' },
  { id: 2, color: '#FF455E' },
  { id: 3, color: '#59EDFF' },
  { id: 4, color: '#55B1F3' },
  { id: 5, color: '#F7C2FF' },
  { id: 6, color: '#FFD252' },
  { id: 7, color: '#98FFE0' },
]

export function formatDuration(seconds) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  const formattedHours = String(hours).padStart(2, '0')
  const formattedMinutes = String(minutes).padStart(2, '0')
  const formattedSeconds = String(Math.round(secs * 100) / 100).padStart(2, '0')

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
}

const formatDate = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const initPagination = {
  page: 1,
  limit: 10,
}

const CallAnalytics = () => {
  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'call_analytics',
    dict: {
      filter: 'Filter',
      search: 'Search',
      start_date: 'Start Date',
      end_date: 'End Date',
      reset_all: 'Reset All',
      apply: 'Apply',
      page_title: 'Call analytics',
      search_placeholder: 'Search',
      tab_duration: 'By duration',
      tab_quantity: 'By quantity',
      incoming: 'Incoming',
      outgoing: 'Outgoing',
      duration_label: 'Duration:',
      average: 'Average',
      total: 'Total',
      last_calls: 'Last calls',
      all: 'All',
      call_back: 'Call back',
      call_date: 'Call date',
      event: 'Event',
      result: 'Result',
      recording: 'Recording a conversation',
      status_call_back: 'Call back',
      status_didnt_reach: "Didn't reach",
      status_conversation: 'Conversation',
      page: 'Page',
      of: 'of',
      full_name: 'Employee’s full name',
      name: 'Employee name',
      period: 'Period ',
    },
  })

  const {
    isOpenFilter,
    changeTextValue,
    closeFilter,
    setTextValues,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  // Общий фильтр (дата, менеджер) — применяется к ОБОИМ запросам
  const [filter, updateFilter] = useLocalStorageFilter({})

  // Отдельный поиск по телефону — только для integration/calls
  const [phoneSearch, setPhoneSearch] = useState('')

  const [analyticsData, setAnalyticsData] = useState(null)
  const [callData, setCallData] = useState(null)
  const [pagination, setPagination] = useState(initPagination)
  const [loading, setLoading] = useState({
    initial: true,
    analytics: false,
    calls: false,
  })
  const [sortBy, setSortBy] = useState('quantity')
  const [callFilter, setCallFilter] = useState('all')

  const buildAnalyticsParams = () => {
    const dateAfter = new Date()
    const dateBefore = new Date()
    dateBefore.setDate(dateBefore.getDate() - 30)

    const params = {
      date_from: filter.date_from ? filter.date_from : formatDate(dateBefore),
      date_until: filter.date_until ? filter.date_until : formatDate(dateAfter),
    }

    if (filter.manager) {
      params.manager_id = filter.manager
    }

    return params
  }

  const buildCallsParams = () => {
    const filterParams = {}
    if (callFilter === 'incoming') {
      filterParams.is_outgoing = false
    } else if (callFilter === 'outgoing') {
      filterParams.is_outgoing = true
    } else if (callFilter === 'callback') {
      filterParams.is_outgoing = false
      filterParams.statuses = ['NOANSWER']
    }

    const params = {
      offset: (pagination.page - 1) * pagination.limit,
      limit: pagination.limit,
      ...filterParams,
    }

    if (phoneSearch) {
      params.phone = phoneSearch
    }
    if (filter.date_from) {
      params.date_from = filter.date_from
    }
    if (filter.date_until) {
      params.date_until = filter.date_until
    }
    if (filter.manager) {
      params.manager_id = filter.manager
    }

    return params
  }

  const fetchCallAnalytics = () => {
    setLoading((prev) => ({ ...prev, analytics: true }))

    superAxiosRequest({
      method: 'get',
      link: 'reports/calls_analytics',
      crm: true,
      params: buildAnalyticsParams(),
    })
      .then(({ data }) => {
        setAnalyticsData(data)
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, analytics: false }))
      })
  }

  const fetchCalls = () => {
    setLoading((prev) => ({ ...prev, calls: true }))

    superAxiosRequest({
      method: 'get',
      link: 'integration/calls',
      crm: true,
      params: buildCallsParams(),
    })
      .then(({ data }) => {
        setCallData(data)
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, calls: false, initial: false }))
      })
  }

  // Аналитика зависит только от общего фильтра
  useEffect(() => {
    fetchCallAnalytics()
  }, [filter])

  // Список звонков — от общего фильтра, поиска по телефону, пагинации и таба
  useEffect(() => {
    fetchCalls()
  }, [pagination, callFilter, filter, phoneSearch])

  // При смене фильтра/поиска сбрасываем страницу на 1
  useEffect(() => {
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [filter, callFilter, phoneSearch])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== `/crm/call-analytics`) {
        updateFilter({})
        setPhoneSearch('')
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  const config = [
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: translate.period,
    },
    {
      type: 'select-multi',
      apiLinkCRM: 'integration/employees/?short_form=true',
      nameState: 'manager',
      label: 'Ответственный',
      placeholder: 'Ответственный',
    },
  ]

  let totalIncomingDuration = 0
  let totalOutgoingDuration = 0
  let totalIncomingCalls = 0
  let totalOutgoingCalls = 0

  if (analyticsData) {
    Object.entries(analyticsData).forEach(([name, details]) => {
      totalIncomingDuration += details?.incoming_calls.duration_seconds || 0
      totalOutgoingDuration += details?.outgoing_calls.duration_seconds || 0
      totalIncomingCalls += details?.incoming_calls.quantity_calls || 0
      totalOutgoingCalls += details?.outgoing_calls.quantity_calls || 0
    })
  }

  const totalDuration = totalIncomingDuration + totalOutgoingDuration

  const averageIncomingCalls = analyticsData
    ? Math.floor(totalIncomingCalls / Object.keys(analyticsData).length)
    : 0
  const averageOutgoingCalls = analyticsData
    ? Math.floor(totalOutgoingCalls / Object.keys(analyticsData).length)
    : 0
  const averageTotalCalls = averageIncomingCalls + averageOutgoingCalls

  const averageIncomingDuration = analyticsData
    ? Math.floor(totalIncomingDuration / Object.keys(analyticsData).length)
    : 0
  const averageOutgoingDuration = analyticsData
    ? Math.floor(totalOutgoingDuration / Object.keys(analyticsData).length)
    : 0
  const averageTotalDuration = averageIncomingDuration + averageOutgoingDuration

  return (
    <div className={st.callAnalyticsContainer}>
      {/* ОБЩИЙ ФИЛЬТР СВЕРХУ — дата + менеджер + AsideFilter, применяется к обоим блокам */}
      <div className={st.header}>
        <PageHeader title={translate?.page_title}></PageHeader>
        <div className={st.filter}>
          <SearchBooks
            onSearch={(text) => {
              updateFilter({ ...filter, name: text })
              changeTextValue('name', {
                key: 'name',
                label: `${translate?.search}...`,
                value: text,
              })
            }}
            onClear={() => {
              const { name: text, ...obj } = filter
              updateFilter(obj)
              setTextValues(
                filterTextValues.filter((field) => field.key !== 'name'),
              )
            }}
            value={filter?.name || ''}
            placeholder={`${translate?.search}...`}
            translate={translate}
          />
          {/*{!!filterTextValues.length && (*/}
          {/*  <FilterTextValues*/}
          {/*    onDelete={(key) => {*/}
          {/*      const { [key]: _, ...obj } = filter*/}
          {/*      updateFilter(obj)*/}
          {/*    }}*/}
          {/*  />*/}
          {/*)}*/}
        </div>
        {isOpenFilter && (
          <AsideFilter
            getFilterParams={(e) => {
              updateFilter(e)
            }}
            config={config}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
            translate={translate}
          />
        )}
      </div>

      {loading.initial ? (
        <Loader />
      ) : (
        <div className={st.container}>
          <div className={st.statisticsContainer}>
            {loading.analytics ? (
              <Loader />
            ) : (
              <>
                {Object.entries(analyticsData || {}).length !== 0 ? (
                  <>
                    <div className={st.filterBtn}>
                      <div
                        onClick={() => setSortBy('duration')}
                        className={sortBy === 'duration' ? st.active : ''}
                      >
                        {translate?.tab_duration}
                      </div>
                      <div
                        onClick={() => setSortBy('quantity')}
                        className={sortBy === 'quantity' ? st.active : ''}
                      >
                        {translate?.tab_quantity}
                      </div>
                    </div>

                    <div className={st.statisticType}>
                      <div>{translate?.incoming}</div>
                      <div className={st.verticalSeparator}></div>
                      <div>{translate?.outgoing}</div>
                    </div>

                    <div className={st.statistics}>
                      {Object.entries(analyticsData).map(
                        ([name, details], index) => (
                          <CallStatistic
                            key={name + index}
                            callColors={callColors}
                            name={name}
                            details={details}
                            index={index}
                            sortBy={sortBy}
                            translate={translate}
                          />
                        ),
                      )}
                    </div>

                    <div className={st.separator}></div>
                    <>
                      <AverageStatistic
                        averageIncomingCalls={averageIncomingCalls}
                        averageOutgoingCalls={averageOutgoingCalls}
                        averageTotalCalls={averageTotalCalls}
                        averageIncomingDuration={averageIncomingDuration}
                        averageOutgoingDuration={averageOutgoingDuration}
                        averageTotalDuration={averageTotalDuration}
                        sortBy={sortBy}
                        translate={translate}
                      />
                      <TotalStatistic
                        totalOutgoingDuration={totalOutgoingDuration}
                        totalIncomingDuration={totalIncomingDuration}
                        totalIncomingCalls={totalIncomingCalls}
                        totalOutgoingCalls={totalOutgoingCalls}
                        totalDuration={totalDuration}
                        sortBy={sortBy}
                        translate={translate}
                      />
                    </>
                  </>
                ) : (
                  <div>Ничего не найдено, поменяйте значения в фильтре.</div>
                )}
              </>
            )}
          </div>

          {/* Блок Last calls — со СВОЕЙ строкой поиска по телефону */}
          <div className={st.header}>
            <PageHeader title={translate?.last_calls || 'Last calls'} />
            <div className={st.filter}>
              <SearchBooks
                isFilterHidden
                onSearch={(text) => setPhoneSearch(text)}
                onClear={() => setPhoneSearch('')}
                value={phoneSearch}
                placeholder={`${translate?.search}...`}
                translate={translate}
              />
            </div>
          </div>

          {loading.calls ? (
            <Loader />
          ) : (
            <CallList
              data={callData?.result}
              callFilter={callFilter}
              setCallFilter={setCallFilter}
              translate={translate}
            />
          )}

          {callData?._meta.count_items > 10 && (
            <Pagination
              translate={translate}
              limit={pagination.limit}
              count={callData._meta.count_items || 0}
              page={pagination.page}
              onChange={(page) => setPagination({ ...pagination, page })}
              onChangePerPage={(limit) => {
                setPagination({ page: 1, limit })
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default observer(CallAnalytics)
